import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { hrApi } from '../../services/hrApi.js';
import { selfApi } from '../../services/selfApi.js';
import { formatDate } from '../../utils/formatters.js';
import { leaveTypeLabel } from '../../utils/leaveTypes.js';

/**
 * Meine Zeit und Ferien.
 *
 * Die Seite, die es vorher nicht gab. Wer bisher seinen Ferienstand sehen
 * wollte, brauchte Zugriff auf die Personalakten aller Kollegen - praktisch
 * hiess das: niemand ausser der Personalabteilung sah irgendetwas.
 *
 * Hier steht ausschliesslich das Eigene. Nicht weil eine Prüfung es filtert,
 * sondern weil die Aufrufe dieser Seite gar keine Mitarbeiterkennung kennen.
 */

const STATUS_VARIANT = {
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'danger',
  CANCELLED: 'neutral',
};

const heute = () => new Date().toISOString().slice(0, 10);

export function MyLeavePage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    leaveTypeId: '',
    startDate: heute(),
    endDate: heute(),
    reason: '',
    startHalfDay: false,
    endHalfDay: false,
  });
  const [error, setError] = useState(null);

  const profile = useQuery({ queryKey: ['self', 'profile'], queryFn: () => selfApi.profile() });
  const balances = useQuery({ queryKey: ['self', 'balances'], queryFn: () => selfApi.leaveBalances() });
  const requests = useQuery({ queryKey: ['self', 'requests'], queryFn: () => selfApi.leaveRequests() });
  const types = useQuery({ queryKey: ['hr', 'leaveTypes'], queryFn: () => hrApi.listLeaveTypes() });

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ['self'] });
  }

  const submit = useMutation({
    mutationFn: () =>
      selfApi.submitLeave({
        leaveTypeId: form.leaveTypeId,
        startDate: form.startDate,
        endDate: form.endDate,
        reason: form.reason.trim() || null,
        startHalfDay: form.startHalfDay,
        endHalfDay: form.endHalfDay,
      }),
    onSuccess: () => {
      refresh();
      setOpen(false);
      setError(null);
      setForm({
        leaveTypeId: '',
        startDate: heute(),
        endDate: heute(),
        reason: '',
        startHalfDay: false,
        endHalfDay: false,
      });
    },
    onError: (err) => setError(err?.message || t('self.leave.submit.failed')),
  });

  /* Die Vorschau. Sie läuft mit, sobald Art und Zeitraum stimmen - die
     Mitarbeiterin erfuhr bisher erst NACH dem Absenden, was ihr Antrag
     kostet, und bei Ferien über Neujahr sogar erst dann, dass er am leeren
     Guthaben des alten Jahres scheitert. */
  const vorschau = useQuery({
    queryKey: [
      'self',
      'leavePreview',
      form.leaveTypeId,
      form.startDate,
      form.endDate,
      form.startHalfDay,
      form.endHalfDay,
    ],
    queryFn: () =>
      selfApi.previewLeave({
        leaveTypeId: form.leaveTypeId,
        startDate: form.startDate,
        endDate: form.endDate,
        startHalfDay: form.startHalfDay,
        endHalfDay: form.endHalfDay,
      }),
    enabled: Boolean(open && form.leaveTypeId && form.startDate && form.endDate && form.endDate >= form.startDate),
    retry: false,
  });

  const withdraw = useMutation({
    mutationFn: (id) => selfApi.withdrawLeave(id),
    onSuccess: refresh,
    onError: (err) => setError(err?.message || t('self.leave.withdraw.failed')),
  });

  /* Ohne verknüpfte Personalakte ist die Seite leer - aber sie sagt WARUM
     und was zu tun ist, statt einfach nichts anzuzeigen. */
  if (profile.error) {
    const code = profile.error?.code;
    return (
      <div className="alert warning">
        {code === 'NO_EMPLOYEE_LINK' ? t('self.noLink') : profile.error.message}
      </div>
    );
  }

  const typeList = types.data?.items || types.data || [];
  const balanceRows = balances.data?.items || balances.data || [];
  const requestRows = requests.data?.items || requests.data || [];

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t('self.title')}</h1>
          <p className="muted">
            {profile.data
              ? t('self.subtitle', {
                  name: `${profile.data.firstName || ''} ${profile.data.lastName || ''}`.trim(),
                })
              : t('self.loading')}
          </p>
        </div>
        <Button variant="primary" onClick={() => setOpen(true)} disabled={!profile.data}>
          {t('self.leave.request')}
        </Button>
      </div>

      {error && <div className="alert danger">{error}</div>}

      <h2 className="card-title">{t('self.balances.title')}</h2>
      <div className="self-balances">
        {balances.isLoading && <p className="muted">{t('self.loading')}</p>}
        {balanceRows.map((b) => (
          <div key={b.leaveTypeId || b.leaveTypeCode} className="self-balance-card">
            <div className="self-balance-name">{leaveTypeLabel(t, b)}</div>
            <div className="self-balance-big num mono">{b.remainingDays}</div>
            <div className="muted text-xs">
              {t('self.balances.of', { granted: b.grantedDays, used: b.usedDays })}
            </div>
          </div>
        ))}
        {!balances.isLoading && balanceRows.length === 0 && (
          <p className="muted">{t('self.balances.empty')}</p>
        )}
      </div>

      <h2 className="card-title">{t('self.requests.title')}</h2>
      <Table
        columns={[
          {
            key: 'period',
            label: t('self.requests.col.period'),
            render: (r) => `${formatDate(r.startDate)} - ${formatDate(r.endDate)}`,
          },
          { key: 'days', label: t('self.requests.col.days'), align: 'right', render: (r) => <span className="num mono">{r.days}</span> },
          {
            key: 'status',
            label: t('self.requests.col.status'),
            render: (r) => (
              <Badge variant={STATUS_VARIANT[r.status] || 'neutral'}>
                {t(`status.${r.status}`)}
              </Badge>
            ),
          },
          { key: 'reason', label: t('self.requests.col.reason'), render: (r) => r.reason || '-' },
          {
            key: 'note',
            label: t('self.requests.col.note'),
            render: (r) => r.decisionNote || '-',
          },
          {
            key: 'actions',
            label: '',
            align: 'right',
            render: (r) =>
              /* Nur offene Anträge. Eine bereits genehmigte Abwesenheit
                 zurückzuziehen ist eine Entscheidung der Vorgesetzten. */
              r.status === 'PENDING' ? (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={withdraw.isPending}
                  onClick={() => withdraw.mutate(r.id)}
                >
                  {t('self.leave.withdraw')}
                </Button>
              ) : null,
          },
        ]}
        rows={requestRows}
        isLoading={requests.isLoading}
        emptyMessage={t('self.requests.empty')}
      />

      <Modal
        open={open}
        title={t('self.leave.request')}
        onClose={() => {
          setOpen(false);
          setError(null);
        }}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={() => submit.mutate()}
              disabled={submit.isPending || !form.leaveTypeId || form.endDate < form.startDate}
            >
              {submit.isPending ? t('self.leave.submitting') : t('self.leave.submit')}
            </Button>
          </>
        }
      >
        <label className="field">
          <span className="field-label">{t('self.leave.type')}</span>
          <select
            className="input"
            value={form.leaveTypeId}
            onChange={(e) => setForm((p) => ({ ...p, leaveTypeId: e.target.value }))}
          >
            <option value="">{t('self.leave.typePlaceholder')}</option>
            {typeList.map((lt) => (
              <option key={lt.id} value={lt.id}>
                {leaveTypeLabel(t, lt)}
              </option>
            ))}
          </select>
        </label>

        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('self.leave.from')}</span>
            <input
              className="input"
              type="date"
              value={form.startDate}
              onChange={(e) => setForm((p) => ({ ...p, startDate: e.target.value }))}
            />
          </label>
          <label className="field">
            <span className="field-label">{t('self.leave.to')}</span>
            <input
              className="input"
              type="date"
              value={form.endDate}
              min={form.startDate}
              onChange={(e) => setForm((p) => ({ ...p, endDate: e.target.value }))}
            />
          </label>
        </div>

        {form.endDate < form.startDate && (
          <p className="muted text-xs">{t('self.leave.orderHint')}</p>
        )}

        {/* Halbe Tage nur an den RÄNDERN. Ein halber Tag mitten in einer
            Ferienwoche ergibt keinen Sinn - man ist entweder da oder nicht. */}
        <div className="field-row">
          <label className="pf-role-option">
            <input
              type="checkbox"
              checked={form.startHalfDay}
              onChange={(e) => setForm((p) => ({ ...p, startHalfDay: e.target.checked }))}
            />
            <span>{t('self.leave.startHalf')}</span>
          </label>
          {form.endDate > form.startDate && (
            <label className="pf-role-option">
              <input
                type="checkbox"
                checked={form.endHalfDay}
                onChange={(e) => setForm((p) => ({ ...p, endHalfDay: e.target.checked }))}
              />
              <span>{t('self.leave.endHalf')}</span>
            </label>
          )}
        </div>

        {/* Was das kostet, und was bleibt. Je Jahr, weil ein Block über
            Neujahr zwei Guthaben belastet. */}
        {vorschau.data && !vorschau.data.allowed && (
          <div className="alert warning">{vorschau.data.reason}</div>
        )}

        {vorschau.data && (
          <div
            className="leave-preview"
            data-short={vorschau.data.sufficient && vorschau.data.allowed ? undefined : 'true'}
          >
            <div className="leave-preview-total">
              <span>{t('self.leave.preview.cost')}</span>
              <strong className="num mono">{Number(vorschau.data.totalDays).toFixed(1)}</strong>
            </div>
            <ul>
              {vorschau.data.byYear.map((j) => (
                <li key={j.year}>
                  <span className="mono">{j.year}</span>
                  {': '}
                  {t('self.leave.preview.year', {
                    cost: Number(j.cost).toFixed(1),
                    before: Number(j.remainingBefore).toFixed(1),
                    after: Number(j.remainingAfter).toFixed(1),
                  })}
                  {!j.sufficient && (
                    <span className="leave-preview-short"> {t('self.leave.preview.short')}</span>
                  )}
                </li>
              ))}
            </ul>
            {/* Was noch mitzubringen ist - und dass dieser Antrag gar nicht
                erst auf eine Freigabe wartet. Eine Krankmeldung ist eine
                Mitteilung, keine Bitte. */}
            {vorschau.data.requiresDocument && (
              <p className="muted text-xs">{t('self.leave.preview.document')}</p>
            )}
            {vorschau.data.requiresApproval === false && (
              <p className="muted text-xs">{t('self.leave.preview.noApproval')}</p>
            )}
          </div>
        )}

        <label className="field">
          <span className="field-label">{t('self.leave.reason')}</span>
          <input
            className="input"
            value={form.reason}
            onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))}
          />
        </label>

        <p className="muted text-xs">{t('self.leave.workdayHint')}</p>

        {error && <div className="alert danger">{error}</div>}
      </Modal>
    </>
  );
}
